import { z } from 'zod';
import { AssistantError, type AssistantConfiguration } from './assistant';
import { catalogProfile } from './catalog-profiles';

export const catalogClaimPolicySchema = z
  .object({
    version: z.literal(1),
    restrictions: z
      .array(
        z
          .object({
            exclusionIndex: z.number().int().nonnegative(),
            kind: z.enum(['catalog_attribute', 'response_behavior']),
            subjectReferences: z
              .array(z.string().trim().min(1).max(160))
              .max(12),
            fields: z.array(z.string().min(1).max(120)).max(30),
          })
          .strict()
      )
      .max(12),
  })
  .strict();
export type CatalogClaimPolicy = z.infer<typeof catalogClaimPolicySchema>;

export function catalogClaimPolicySource(
  configuration: AssistantConfiguration
) {
  const profile = catalogProfile(
    configuration.catalog?.profile ?? 'property',
    configuration.catalog
  );
  return {
    version: 1,
    exclusions: configuration.salesPlaybook?.excludedClaims ?? [],
    attributes: [
      ...profile.attributes,
      {
        key: 'priceAmounts',
        label: 'Precios publicados por moneda',
        type: 'money',
      },
    ],
    referenceTerms: profile.referenceTerms,
  };
}

export function validateCatalogClaimPolicy(
  value: unknown,
  configuration: AssistantConfiguration
): CatalogClaimPolicy {
  const policy = catalogClaimPolicySchema.parse(value);
  const source = catalogClaimPolicySource(configuration);
  const indexes = new Set(
    policy.restrictions.map((rule) => rule.exclusionIndex)
  );
  if (
    indexes.size !== source.exclusions.length ||
    policy.restrictions.length !== source.exclusions.length
  )
    throw new AssistantError('CATALOG_CLAIM_POLICY_INCOMPLETE', 409);
  for (const rule of policy.restrictions) {
    const exclusion = source.exclusions[rule.exclusionIndex];
    if (
      !exclusion ||
      rule.subjectReferences.some(
        (reference) =>
          !exclusion.toLocaleLowerCase().includes(reference.toLocaleLowerCase())
      ) ||
      rule.fields.some(
        (field) =>
          !source.attributes.some((attribute) => attribute.key === field)
      ) ||
      (rule.kind === 'catalog_attribute' && !rule.fields.length) ||
      (rule.kind === 'response_behavior' &&
        (rule.fields.length || rule.subjectReferences.length))
    )
      throw new AssistantError('CATALOG_CLAIM_POLICY_INVALID', 409);
  }
  // The source may name a product as “unidad 402”; the catalog matches its
  // code. Strip only the configured product term, preserving the code itself.
  return {
    ...policy,
    restrictions: policy.restrictions.map((rule) => ({
      ...rule,
      subjectReferences: rule.subjectReferences.map((reference) => {
        const prefix = source.referenceTerms.find((term) =>
          reference
            .toLocaleLowerCase()
            .startsWith(`${term.toLocaleLowerCase()} `)
        );
        return prefix ? reference.slice(prefix.length).trim() : reference;
      }),
    })),
  };
}
